import React from "react";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import {
  Home, Search, Calendar, Bell, User, LayoutDashboard, Clock, Stethoscope,
  Users, ClipboardList, Tag, LogOut, Menu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const patientNav = [
  { to: "/patient/dashboard", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/doctors", label: "Find", icon: Search, testid: "nav-find" },
  { to: "/patient/appointments", label: "Appointments", icon: Calendar, testid: "nav-appointments" },
  { to: "/patient/notifications", label: "Alerts", icon: Bell, testid: "nav-notifications" },
  { to: "/patient/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

const doctorNav = [
  { to: "/doctor/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/doctor/appointments", label: "Appointments", icon: Calendar, testid: "nav-appointments" },
  { to: "/doctor/availability", label: "Availability", icon: Clock, testid: "nav-availability" },
  { to: "/doctor/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

const adminNav = [
  { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/admin/doctors", label: "Doctors", icon: Stethoscope, testid: "nav-doctors" },
  { to: "/admin/patients", label: "Patients", icon: Users, testid: "nav-patients" },
  { to: "/admin/appointments", label: "Appointments", icon: ClipboardList, testid: "nav-appointments" },
  { to: "/admin/specialties", label: "Specialties", icon: Tag, testid: "nav-specialties" },
];

function getNav(role) {
  if (role === "doctor") return doctorNav;
  if (role === "admin") return adminNav;
  return patientNav;
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = getNav(user?.role);
  const location = useLocation();
  const navigate = useNavigate();

  const initials = (user?.name || user?.email || "U")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 flex-col z-30">
        <Link to="/" className="px-6 py-6 flex items-center gap-2" data-testid="sidebar-logo">
          <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center font-bold">D</div>
          <span className="text-xl font-bold tracking-tight text-slate-900">DocCare</span>
        </Link>
        <nav className="flex-1 px-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`side-${n.testid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center text-sm font-semibold">{initials}</div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate" data-testid="side-user-name">{user?.name || "User"}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full rounded-xl"
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="btn-logout"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-slate-900 text-white grid place-items-center font-bold text-sm">D</div>
          <span className="text-lg font-bold text-slate-900">DocCare</span>
        </Link>
        <Button size="sm" variant="ghost" onClick={async () => { await logout(); navigate("/"); }} data-testid="btn-mobile-logout">
          <LogOut className="w-4 h-4" />
        </Button>
      </header>

      {/* Main */}
      <main className="lg:pl-64 pb-24 lg:pb-8">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8" key={location.pathname}>
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-40" data-testid="mobile-bottom-nav">
        <div className="grid grid-cols-5">
          {nav.slice(0, 5).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`bot-${n.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                  isActive ? "text-slate-900" : "text-slate-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon className={`w-5 h-5 ${isActive ? "text-emerald-600" : ""}`} />
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
